import { useThree } from '@react-three/fiber';
import { useEffect, useRef } from 'react';
import { filter, fromEvent, map, merge, switchMap, takeUntil, tap } from 'rxjs';
import { Plane, Raycaster, Vector2, Vector3, type Vector3Tuple } from 'three';

import { useAllObjectClassListQuery } from '@/hook';
import { TrackIDManager, trackIDManager } from '@/manager';
import { useSubscriptions, useUnleash, useVToast, uuid } from '@/package';
import { useLabelingBoxContext } from '@/provider';
import {
  useBoxClipboardStore,
  useLabelingBoxStore,
  useReviewStore,
  useToolMenuStore,
  useToolPermissionStore,
} from '@/store';
import type { BoxWithLabel } from '@/type';
import {
  eulerToQuaternion,
  getPlanePoint,
  withCanvasXY,
  withTarget,
} from '@/util';

export const useRemoveBoxListShortcut = () => {
  const { toolMap, findByName } = useToolMenuStore();
  const { getCurrentPcdIndexReviewBoxList, setReviewBoxListWithIndex } =
    useReviewStore();
  const reviewBoxList = getCurrentPcdIndexReviewBoxList();
  const {
    selectedLabelingBoxList: selectedBoxList,
    getCurrentBoxList,
    removeBox,
  } = useLabelingBoxContext();
  const { permission } = useToolPermissionStore();
  const { reviewBoxMode } = useReviewStore();
  const enabledAddReviewBox =
    permission.labelingReview.updatable && reviewBoxMode;

  const removeTool = findByName('labelBoxRemove')!;

  useEffect(() => {
    if (!removeTool.isSelected) return;

    const isSelectedBox = (box: BoxWithLabel) =>
      // selectedBoxList.find(b => b.id.value === box.id);
      selectedBoxList.find(b => b.id === box.id);
    const newReviewBoxList = reviewBoxList.filter(box => !isSelectedBox(box));

    setReviewBoxListWithIndex(newReviewBoxList);

    getCurrentBoxList().forEach(box => {
      isSelectedBox(box) && removeBox(box.id);
    });
  }, [toolMap, selectedBoxList, enabledAddReviewBox]);
};

export const useClipboardShortcut = () => {
  const {
    labelingBoxList,
    selectedBoxIdMap,
    getCurrentBoxList,
    getCurrentBoxIdMap,
    selectBoxList,
    setBoxList,
  } = useLabelingBoxContext();
  const copiedBoxList = useBoxClipboardStore(state => state.copiedBoxList);
  const toolMap = useToolMenuStore(state => state.toolMap);
  const { camera, gl, raycaster } = useThree();
  const { isEnabled } = useUnleash();
  const latestCanvasXYPoint = useRef([null, null] as [
    Nullish<number>,
    Nullish<number>,
  ]);
  const labelingBoxTrackIDSet = useRef(new Set<string>());
  const isEnabledTrackIDManager = isEnabled(
    'vuex.sapporo.enabledTrackIDManager',
  );

  const copyTool = useToolMenuStore.getState().findByName('labelBoxCopy')!;
  const pasteTool = useToolMenuStore.getState().findByName('labelBoxPaste')!;

  const pasteBoxList = (copiedBoxList: BoxWithLabel[]) => {
    const boxIdMap = getCurrentBoxIdMap();
    const willAddBoxList = copiedBoxList.map(box => {
      let trackId = box.trackId;

      if (
        (isEnabledTrackIDManager &&
          trackId &&
          labelingBoxTrackIDSet.current.has(trackId)) ||
        !TrackIDManager.isValidTrackID(trackId)
      ) {
        trackId = trackIDManager.trackID;
        trackIDManager.next();
      }

      return {
        ...box,
        confidence: 1, // pasteBoxList 는 user 액션에 의한 호출이므로 confidence 를 1로 고정해주어야 함
        id: boxIdMap[box.id] ? uuid() : box.id,
        trackId,
      };
    });

    setBoxList([...getCurrentBoxList(), ...willAddBoxList]);
    selectBoxList(willAddBoxList.map(box => box.id));
  };

  useEffect(() => {
    labelingBoxTrackIDSet.current = new Set(
      labelingBoxList.filter(box => box.trackId).map(box => box.trackId!),
    );
  }, [labelingBoxList]);

  useEffect(() => {
    if (!copyTool.isSelected) return;

    const boxList = getCurrentBoxList().filter(box => selectedBoxIdMap[box.id]);

    boxList.length && useBoxClipboardStore.getState().copy(boxList);
  }, [toolMap, selectedBoxIdMap]);

  useEffect(() => {
    if (!pasteTool.isSelected) return;

    const [x, y] = latestCanvasXYPoint.current;

    useBoxClipboardStore
      .getState()
      .paste()
      .then(boxList => {
        const [first, ...otherBoxList] = boxList;

        if (first && !otherBoxList.length && x != null && y != null) {
          const { x: nX, y: nY } = getPlanePoint({
            x,
            y,
            camera,
            raycaster,
          });

          first.position[0] = nX;
          first.position[1] = nY;
        }

        boxList.length && pasteBoxList(boxList);
      });
  }, [copiedBoxList, toolMap]);

  useSubscriptions(
    () => [
      // pointermove, pointerdown 을 merge 한 뒤, null이 아닌 값으로 병합하는 rx stream 작성
      merge(
        fromEvent<MouseEvent>(window, 'pointermove'),
        fromEvent<MouseEvent>(window, 'pointerdown'),
      )
        .pipe(
          withTarget,
          withCanvasXY,
          map(e =>
            e.target === gl.domElement ? [e.canvasX, e.canvasY] : [null, null],
          ),
          filter(([x, y]) => x != null && y != null),
          tap(([x, y]) => (latestCanvasXYPoint.current = [x, y])),
        )
        .subscribe(),
    ],
    [],
  );
};

export const useToggleBoxArrowHelper = () => {
  const { flagList, isEnabled } = useUnleash();
  const toolMap = useToolMenuStore(state => state.toolMap);

  useEffect(() => {
    const arrowTool = useToolMenuStore.getState().findByName('labelBoxArrow');

    if (!arrowTool || !isEnabled('vuex.sapporo.visibleArrowInLabelingTool'))
      return;

    useLabelingBoxStore.getState().setVisibleArrowHelper(arrowTool.isSelected);
  }, [flagList, toolMap]);

  useEffect(() => {
    if (isEnabled('vuex.sapporo.visibleArrowInLabelingTool')) return;

    useToolMenuStore.getState().deselectByName('labelBoxArrow');
  }, [flagList]);
};

export type useChangeTransformModeShortcutProps = {
  initTransformMode?: () => any;
};

export const useAddBox = () => {
  const { reviewBoxMode, addBox: addReviewBox } = useReviewStore();
  const { addBox } = useLabelingBoxContext();
  const { permission } = useToolPermissionStore();
  const { toolMap, getSelectedToolMenuList, isSelectedByToolName } =
    useToolMenuStore();
  const { camera, gl } = useThree();
  const enabledAddReviewBox =
    permission.labelingReview.updatable && reviewBoxMode;
  const { data: allObjectClassList } = useAllObjectClassListQuery();
  const enabledAddBox =
    permission.labeling.updatable ||
    enabledAddReviewBox ||
    (allObjectClassList?.length ?? 0) > 0;

  useSubscriptions(
    () => [
      fromEvent<MouseEvent>(window, 'mousemove')
        .pipe(
          withTarget,
          withCanvasXY,
          filter(() => enabledAddBox),
          switchMap(mouseMoveEvent => {
            return fromEvent<MouseEvent>(window, 'mousedown').pipe(
              filter(
                e =>
                  e.target?.constructor === HTMLCanvasElement &&
                  (e.target as HTMLCanvasElement).style.cursor !== 'pointer' &&
                  e.buttons === 1,
              ),
              map(e => ({ x: e.pageX, y: e.pageY })),
              switchMap(({ x, y }) => {
                const [start, end, diff] = [
                  { x, y },
                  { x, y },
                  {
                    get x() {
                      return Math.abs(end.x - start.x);
                    },
                    get y() {
                      return Math.abs(end.y - start.y);
                    },
                  },
                ];

                return fromEvent<MouseEvent>(window, 'mousemove').pipe(
                  tap(e => {
                    end.x = e.pageX;
                    end.y = e.pageY;
                  }),
                  takeUntil(
                    fromEvent<MouseEvent>(window, 'mouseup').pipe(
                      filter(() => diff.x < 3 && diff.y < 3),
                      tap(() => {
                        const enableDrawing =
                          getSelectedToolMenuList().length > 0 &&
                          isSelectedByToolName('box');

                        if (!enableDrawing) return;

                        const point = new Vector3();
                        const raycaster = new Raycaster();
                        const mouse = new Vector2();
                        const rect = gl.domElement.getBoundingClientRect();
                        const plane = new Plane(new Vector3(0, 0, 1), 0);

                        mouse.x =
                          ((mouseMoveEvent.clientX - rect.left) / rect.width) *
                            2 -
                          1;
                        mouse.y =
                          -((mouseMoveEvent.clientY - rect.top) / rect.height) *
                            2 +
                          1;

                        raycaster.setFromCamera(mouse, camera);
                        raycaster.ray.intersectPlane(plane, point);

                        const position = point.toArray() as Vector3Tuple;
                        const label =
                          getSelectedToolMenuList()
                            .find(toolMenu => toolMenu.name.match(/box\d+/))
                            ?.name.replace('box', '') ?? '';
                        const dimension = allObjectClassList?.find(
                          item => +item.id === +label,
                        )?.size ?? [1, 1, 1];

                        const box = {
                          position,
                          label,
                          dimension: dimension as Vector3Tuple,
                          rotation: [0, 0, 0] as Vector3Tuple,
                          quaternion: eulerToQuaternion([0, 0, 0]),
                        };

                        if (enabledAddReviewBox) addReviewBox(box);
                        else addBox(box);
                      }),
                    ),
                  ),
                );
              }),
            );
          }),
        )
        .subscribe(),
    ],
    [
      camera,
      gl,
      addBox,
      enabledAddBox,
      enabledAddReviewBox,
      reviewBoxMode,
      toolMap,
    ],
  );
};

export const useBoxFocusShortcut = () => {
  const {
    selectedLabelingBoxList: selectedBoxList,
    selectNextBox,
    selectPrevBox,
  } = useLabelingBoxContext();
  const toolMap = useToolMenuStore(state => state.toolMap);

  useEffect(() => {
    const isSelectedByToolName =
      useToolMenuStore.getState().isSelectedByToolName;

    if (isSelectedByToolName('focusPrevBox')) {
      selectPrevBox();
    }

    if (isSelectedByToolName('focusNextBox')) {
      selectNextBox();
    }
  }, [toolMap, selectedBoxList]);
};

export const useTrackIDShortcut = () => {
  const {
    selectedLabelingBoxList: selectedBoxList,
    changeBoxTrackID,
    findBoxByTrackID,
  } = useLabelingBoxContext();
  const toolMap = useToolMenuStore(state => state.toolMap);
  const copiedTrackIDRef = useRef<Nullish<string>>(null);
  const { addToast } = useVToast();

  useEffect(() => {
    const isSelectedByToolName =
      useToolMenuStore.getState().isSelectedByToolName;

    const trackID = selectedBoxList?.[0]?.trackId;

    if (
      isSelectedByToolName('copyTrackID') &&
      selectedBoxList.length >= 1 &&
      trackID!
    ) {
      if (TrackIDManager.isValidTrackID(trackID)) {
        useBoxClipboardStore.getState().copyTrackID(trackID);
        copiedTrackIDRef.current = trackID;
      } else {
        addToast({
          autoClose: true,
          variant: 'error',
          message: 'Track ID is invalid',
        });
      }
    }

    if (
      isSelectedByToolName('pasteTrackID') &&
      selectedBoxList.length >= 1 &&
      copiedTrackIDRef.current &&
      TrackIDManager.isValidTrackID(copiedTrackIDRef.current)
    ) {
      const trackID = copiedTrackIDRef.current;
      const boxID = selectedBoxList?.[0]?.id;

      if (!boxID) return;

      const findedBox = findBoxByTrackID(trackID);

      if (findedBox) {
        addToast({
          autoClose: true,
          variant: 'error',
          message: 'Track ID is already exists',
        });

        return;
      }

      changeBoxTrackID(boxID, trackID);
    }
  }, [toolMap, selectedBoxList]);
};
